import { useState } from 'react'
import type { WeeklySchedule, TimeSlot } from '@/types/booking'
import { MonthlyAvailability } from './MonthlyAvailability'
import { WeeklyAvailability } from './WeeklyAvailability'
import { useBookingSystem } from '@/contexts/BookingSystemContext'
import { TimePickerProvider } from '@/components/ui/TimePickerContext'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type ViewTab = 'week' | 'month'

export function AvailabilityTabs() {
  const { availability, updateAvailability, updateAvailabilityMode, updateDateOverrides, updateMonthSchedule } = useBookingSystem()
  
  // Determine default tab based on availability mode
  const defaultMode = availability?.mode || 'week'
  const hasWeekAvailability = availability?.weekSchedule && 
    Object.values(availability.weekSchedule).some(day => day.isEnabled && day.timeSlots.length > 0)
  
  const [activeTab, setActiveTab] = useState<ViewTab>(defaultMode === 'week' && hasWeekAvailability ? 'week' : 'month')
  const [isUpdatingMode, setIsUpdatingMode] = useState(false)

  const handleSaveWeeklySchedule = async (newSchedule: WeeklySchedule) => {
    try {
      const currentMode = availability?.mode || 'week'
      await updateAvailability(newSchedule, currentMode)
    } catch (error) {
      console.error('Failed to update schedule:', error)
      throw error
    }
  }

  const handleSaveMonthDates = async (dates: Date[], timeSlots: TimeSlot[], isUnavailable: boolean) => {
    try {
      await updateMonthSchedule(dates, timeSlots, isUnavailable)
    } catch (error) {
      console.error('Failed to update month schedule:', error)
      throw error
    }
  }

  const handleSaveSpecificDates = async (dates: Date[], timeSlots: TimeSlot[]) => {
    try {
      await updateDateOverrides(dates, timeSlots)
    } catch (error) {
      console.error('Failed to update specific dates:', error)
      throw error
    }
  }

  const handleSetActiveMode = async (mode: 'month' | 'week') => {
    try {
      setIsUpdatingMode(true)
      await updateAvailabilityMode(mode)
      toast.success(`${mode.charAt(0).toUpperCase() + mode.slice(1)} mode is now active`)
    } catch (error) {
      console.error('Failed to update mode:', error)
      toast.error('Failed to update mode')
    } finally {
      setIsUpdatingMode(false)
    }
  }

  return (
    <TimePickerProvider>
      <div className="flex flex-col flex-1 min-h-0">
        {/* Tab Navigation - Fixed */}
        <div className="flex-shrink-0 pb-4">
          <div className="phone-container-padded">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('month')}
                className={cn(
                  "flex-1 px-4 py-3 font-semibold text-sm transition-all rounded-lg border-2 relative",
                  activeTab === 'month'
                    ? 'border-primary bg-primary text-primary-foreground shadow-md'
                    : 'border-border text-muted-foreground hover:border-primary hover:text-foreground'
                )}
              >
                Month
                {availability?.mode === 'month' && (
                  <div className="absolute top-2 right-2 w-2 h-2 bg-green-500 rounded-full" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('week')}
                className={cn(
                  "flex-1 px-4 py-3 font-semibold text-sm transition-all rounded-lg border-2 relative",
                  activeTab === 'week'
                    ? 'border-primary bg-primary text-primary-foreground shadow-md'
                    : 'border-border text-muted-foreground hover:border-primary hover:text-foreground'
                )}
              >
                Week
                {availability?.mode === 'week' && (
                  <div className="absolute top-2 right-2 w-2 h-2 bg-green-500 rounded-full" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="phone-container-padded pb-8">
            {activeTab === 'month' && (
              <div className="flex flex-col gap-4">
                <MonthlyAvailability
                  availability={availability}
                  onSave={handleSaveMonthDates}
                  useMonthSchedule={true}
                />
                <Button
                  onClick={() => handleSetActiveMode('month')}
                  disabled={isUpdatingMode || availability?.mode === 'month'}
                  className="w-full"
                  size="lg"
                >
                  {availability?.mode === 'month' ? 'Active' : 'Set as Active'}
                </Button>
              </div>
            )}

            {activeTab === 'week' && (
              <div className="flex flex-col gap-2">
                <WeeklyAvailability
                  availability={availability}
                  onSave={handleSaveWeeklySchedule}
                  onSaveSpecificDates={handleSaveSpecificDates}
                />
                <Button
                  onClick={() => handleSetActiveMode('week')}
                  disabled={isUpdatingMode || availability?.mode === 'week'}
                  className="w-full"
                  size="lg"
                >
                  {availability?.mode === 'week' ? 'Active' : 'Set as Active'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </TimePickerProvider>
  )
}
